"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useAICheckStore } from "@/stores/aicheck";
import type { RunProgress as RunProgressType } from "@/types/aicheck";

interface RunProgressProps {
  progress: RunProgressType;
  progressThresholdBytes?: number;
  onCancel?: () => void;
  onRetry?: () => void;
  onRetryFailed?: () => void;
  onDismiss?: () => void;
}

const PHASE_LABELS: Record<string, string> = {
  "fetching-data": "Fetching project data...",
  // "compacting-contacts" intentionally omitted — uses dynamic step text
  // from the server (e.g. "Compacting contacts: 2024/1/15 ~ 2024/3/20 (1/3)")
  "extracting-profile": "Extracting customer profile...",
  "running-ai": "Running AI analysis...",
};

// Progress allocation across statuses:
// fetch(0-8) -> compact(8-15) -> profile(15-25) -> AI checks (25-100)
// consistency is reflected in final results, not as a dedicated progress status
const PHASE_BASE_PCT: Record<string, number> = {
  "fetching-data": 8,
  "compacting-contacts": 12,
  "extracting-profile": 18,
  "running-ai": 25,
};

const COMPLETION_VISIBLE_MS = 2000;

function useElapsedTime(startedAt: number | undefined, isActive: boolean) {
  const [elapsed, setElapsed] = useState("");

  useEffect(() => {
    if (!startedAt || !isActive) {
      setElapsed("");
      return;
    }

    const update = () => {
      const totalSeconds = Math.floor((Date.now() - startedAt) / 1000);
      if (totalSeconds < 60) {
        setElapsed(`${totalSeconds}s`);
      } else {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        setElapsed(`${minutes}m ${seconds}s`);
      }
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startedAt, isActive]);

  return elapsed;
}

const THRESHOLD_BYTES = 18 * 1024; // 18 KB linear zone (payload size)
const C_EXCESS = (20 - 18) * 1024; // 2,048 bytes (excess at 20 KB)
const P_EXP = 0.32;

/** Linear to 90% at the byte threshold, then asymptotic; never reaches 100% while streaming. */
function calcBarPercent(
  bytesReceived: number,
  thresholdBytes = THRESHOLD_BYTES,
): number {
  if (bytesReceived <= thresholdBytes) {
    return (bytesReceived / thresholdBytes) * 90;
  }
  const excess = bytesReceived - thresholdBytes;
  const excessScale = (thresholdBytes / THRESHOLD_BYTES) * C_EXCESS;
  const ratio = (excess / excessScale) ** P_EXP;
  return 90 + 10 * (1 - 1 / (1 + ratio));
}

function calcCheckProgressPercent(
  check: CheckDisplayInfo,
  thresholdBytes = THRESHOLD_BYTES,
): number {
  return calcBarPercent(
    check.bytesReceived ?? check.contentLength ?? 0,
    thresholdBytes,
  );
}

interface CheckDisplayInfo {
  checkName: string;
  phase: string;
  tokensReceived?: number;
  bytesReceived?: number;
  contentLength?: number;
  attempt?: number;
}

function CheckRow({
  check,
  isCompleting,
  isPending,
  isFailed,
  isSkipped,
  errorMessage,
  progressThresholdBytes,
}: {
  check: CheckDisplayInfo;
  isCompleting?: boolean;
  isPending?: boolean;
  isFailed?: boolean;
  isSkipped?: boolean;
  errorMessage?: string;
  progressThresholdBytes?: number;
}) {
  const isRetrying = check.attempt != null && check.attempt >= 2;
  const isStreaming = check.phase === "streaming";
  const hasTokens = isStreaming && !!check.tokensReceived;
  const hasStreamingProgress =
    isStreaming &&
    (hasTokens ||
      (check.bytesReceived ?? 0) > 0 ||
      (check.contentLength ?? 0) > 0);
  const bytesForDisplay = check.bytesReceived ?? check.contentLength ?? 0;
  const barPercent = isCompleting
    ? 100
    : calcCheckProgressPercent(check, progressThresholdBytes);
  const showBar =
    !isPending &&
    !isFailed &&
    !isSkipped &&
    (hasStreamingProgress ||
      (isCompleting === true && (check.contentLength ?? 0) > 0));

  return (
    <div
      className={`text-xs flex items-center gap-1.5 ${
        isFailed
          ? "text-red-500"
          : isPending || isCompleting || isSkipped
            ? "text-gray-400"
            : "text-gray-500"
      }`}
    >
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${
          isFailed
            ? "bg-red-500"
            : isSkipped
              ? "bg-gray-400"
              : isPending
                ? "bg-red-400"
                : isCompleting
                  ? "bg-gray-300"
                  : isStreaming
                    ? "bg-emerald-500 animate-pulse"
                    : check.phase === "waiting"
                      ? "bg-amber-400 animate-pulse"
                      : "bg-gray-300"
        }`}
      />

      <span
        className={`font-semibold ${
          isFailed
            ? "text-red-600"
            : isPending || isCompleting || isSkipped
              ? "text-gray-400"
              : "text-gray-700"
        }`}
      >
        {check.checkName}
      </span>
      <span>:</span>

      {isFailed ? (
        <span className="text-red-500 truncate" title={errorMessage}>
          Failed{errorMessage ? ` — ${errorMessage}` : ""}
        </span>
      ) : isSkipped ? (
        <span className="text-gray-400">Pending</span>
      ) : isPending ? (
        <span className="text-gray-400">Pending</span>
      ) : isCompleting ? (
        <span className="text-gray-400">Done</span>
      ) : isRetrying ? (
        <span>Retrying... (attempt {check.attempt})</span>
      ) : check.phase === "waiting" ? (
        <span>Waiting for AI...</span>
      ) : hasTokens ? (
        <span>Generating...</span>
      ) : (
        <span>Processing...</span>
      )}

      {showBar && !isCompleting && (
        <span className="tabular-nums whitespace-nowrap">
          ({hasTokens ? `${check.tokensReceived} tokens, ` : ""}
          {(bytesForDisplay / 1024).toFixed(1)} KB)
        </span>
      )}

      {showBar && (
        <span className="ml-auto inline-flex items-center w-16 h-1.5 rounded-full bg-gray-200 overflow-hidden shrink-0">
          <span
            className={`h-full rounded-full transition-all duration-300 ${isCompleting ? "bg-gray-300" : "bg-emerald-400"}`}
            style={{ width: `${Math.max(barPercent, 2)}%` }}
          />
        </span>
      )}
    </div>
  );
}

export function RunProgress({
  progress,
  progressThresholdBytes = THRESHOLD_BYTES,
  onCancel,
  onRetry,
  onRetryFailed,
  onDismiss,
}: RunProgressProps) {
  const [showCompletion, setShowCompletion] = useState(false);
  const [expanded, setExpanded] = useState(true);
  // ⑤ 実況ログは runProgress とは別の store フィールドから読む（進捗グラフを揺らさない）。
  const liveLog = useAICheckStore((s) => s.liveLog);

  const isActive = ![
    "idle",
    "completed",
    "error",
    "cancelled",
    "disconnected",
  ].includes(progress.status);

  const elapsed = useElapsedTime(progress.startedAt, isActive);

  const displayChecks = useMemo(() => {
    const activeNames = new Set(
      (progress.activeChecks ?? []).map((c) => c.checkName),
    );
    const completedNames = new Set(progress.completedCheckNames ?? []);
    const failedMap = new Map(
      (progress.failedChecks ?? []).map((f) => [f.checkName, f.error]),
    );
    const skippedNames = new Set(
      (progress.skippedItems ?? []).map((s) => s.name),
    );

    type DisplayEntry = {
      checkName: string;
      phase: string;
      isCompleting: boolean;
      isPending: boolean;
      isFailed: boolean;
      isSkipped: boolean;
      errorMessage?: string;
      tokensReceived?: number;
      bytesReceived?: number;
      contentLength?: number;
      attempt?: number;
    };

    const checkMap = new Map<string, DisplayEntry>();

    // Skipped items (from server event)
    for (const item of progress.skippedItems ?? []) {
      checkMap.set(item.name, {
        checkName: item.name,
        phase: "waiting",
        isCompleting: false,
        isPending: false,
        isFailed: false,
        isSkipped: true,
      });
    }

    // Pending = planned but not active and not completed
    for (const name of progress.plannedChecks ?? []) {
      if (
        !activeNames.has(name) &&
        !completedNames.has(name) &&
        !skippedNames.has(name)
      ) {
        checkMap.set(name, {
          checkName: name,
          phase: "waiting",
          isCompleting: false,
          isPending: true,
          isFailed: false,
          isSkipped: false,
        });
      }
    }

    // Completed (overrides pending)
    for (const name of completedNames) {
      if (!activeNames.has(name)) {
        if (failedMap.has(name)) {
          checkMap.set(name, {
            checkName: name,
            phase: "waiting",
            isCompleting: false,
            isPending: false,
            isFailed: true,
            isSkipped: false,
            errorMessage: failedMap.get(name),
          });
        } else {
          checkMap.set(name, {
            checkName: name,
            phase: "waiting",
            isCompleting: true,
            isPending: false,
            isFailed: false,
            isSkipped: false,
          });
        }
      }
    }

    // Active (overrides all)
    for (const c of progress.activeChecks ?? []) {
      checkMap.set(c.checkName, {
        ...c,
        isCompleting: false,
        isPending: false,
        isFailed: false,
        isSkipped: false,
      });
    }

    // Order: plannedChecks is append-only in the store, so its order
    // is the canonical insertion order. New items always appear at bottom.
    const ordered: string[] = [];
    const seen = new Set<string>();
    for (const name of progress.plannedChecks ?? []) {
      if (checkMap.has(name) && !seen.has(name)) {
        ordered.push(name);
        seen.add(name);
      }
    }
    // Skipped items that weren't in plannedChecks
    for (const item of progress.skippedItems ?? []) {
      if (!seen.has(item.name) && checkMap.has(item.name)) {
        ordered.push(item.name);
        seen.add(item.name);
      }
    }
    // Items that appeared in activeChecks before planned-checks arrived
    for (const name of checkMap.keys()) {
      if (!seen.has(name)) {
        ordered.push(name);
        seen.add(name);
      }
    }

    return ordered.map((name) => {
      const check = checkMap.get(name);
      if (!check) throw new Error(`Missing check: ${name}`);
      return check;
    });
  }, [
    progress.activeChecks,
    progress.plannedChecks,
    progress.completedCheckNames,
    progress.failedChecks,
    progress.skippedItems,
  ]);

  useEffect(() => {
    if (progress.status === "completed") {
      setShowCompletion(true);
      const timer = setTimeout(
        () => setShowCompletion(false),
        COMPLETION_VISIBLE_MS,
      );
      return () => clearTimeout(timer);
    }
    setShowCompletion(false);
  }, [progress.status]);

  const hasFailures =
    !!progress.failedStage || (progress.failedChecks?.length ?? 0) > 0;
  const [skippedExpanded, setSkippedExpanded] = useState(false);

  if (progress.status === "idle") return null;
  if (progress.status === "completed" && !showCompletion && !hasFailures)
    return null;

  // Completion state — failed
  if (progress.status === "completed" && hasFailures) {
    const failureMessage =
      progress.stageError ??
      (progress.failedChecks?.length
        ? `${progress.failedChecks.length} check${progress.failedChecks.length !== 1 ? "s" : ""} failed`
        : "Check halted");
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-amber-700 font-medium">
            <span>Check halted — {failureMessage}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            {onRetryFailed && (
              <button
                type="button"
                onClick={onRetryFailed}
                className="text-sm font-medium text-amber-700 hover:text-amber-900 underline"
              >
                Retry
              </button>
            )}
            {onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                title="Dismiss"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M1 1l12 12M13 1L1 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
        {(progress.skippedItems?.length ?? 0) > 0 && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setSkippedExpanded(!skippedExpanded)}
              className="text-xs text-amber-600 hover:text-amber-800"
            >
              {skippedExpanded ? "Hide" : "Show"}{" "}
              {progress.skippedItems?.length ?? 0} skipped item
              {progress.skippedItems?.length !== 1 ? "s" : ""}
            </button>
            {skippedExpanded && (
              <div className="mt-1 flex flex-col gap-0.5">
                {progress.skippedItems?.map((item) => (
                  <div
                    key={item.id}
                    className="text-xs text-amber-600 flex items-center gap-1.5"
                  >
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-gray-400 shrink-0" />
                    <span>{item.name}</span>
                    <span className="text-amber-400">({item.kind})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-amber-200">
          <div className="h-full rounded-full bg-amber-400 w-3/4" />
        </div>
      </div>
    );
  }

  // Completion state — success
  if (progress.status === "completed") {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 transition-opacity duration-500">
        <div className="flex items-center gap-2 text-sm text-green-700 font-medium">
          <span>Check complete</span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-green-200">
          <div className="h-full rounded-full bg-green-500 w-full" />
        </div>
      </div>
    );
  }

  // Cancelled state
  if (progress.status === "cancelled") {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-amber-700 font-medium">
            <span>Check was cancelled.</span>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="text-sm font-medium text-amber-700 hover:text-amber-900 underline"
              >
                Run again
              </button>
            )}
            {onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                title="Dismiss"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M1 1l12 12M13 1L1 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-amber-200">
          <div className="h-full rounded-full bg-amber-400 w-1/2" />
        </div>
      </div>
    );
  }

  const isRunning =
    progress.status !== "error" && progress.status !== "disconnected";

  // Status-based progress: pre-AI statuses use fixed baselines, AI checks fill the remainder.
  const basePct = PHASE_BASE_PCT[progress.status] ?? 0;
  const activeCheckFraction =
    progress.status === "running-ai" && progress.totalChecks > 0
      ? (progress.activeChecks ?? []).reduce(
          (sum, check) =>
            sum + calcCheckProgressPercent(check, progressThresholdBytes) / 100,
          0,
        )
      : 0;
  const checkPct =
    progress.totalChecks > 0
      ? ((progress.completedChecks + activeCheckFraction) /
          progress.totalChecks) *
        75
      : 0;
  const pct =
    progress.status === "running-ai"
      ? Math.min(98, Math.round(basePct + checkPct))
      : basePct;

  // Prefer caller-provided step text so conversion progress can describe
  // extraction or reservation matching instead of generic AI check work.
  const stepLabel = progress.currentStep || PHASE_LABELS[progress.status];

  // Collapsed: progress bar + count + cancel only
  if (!expanded && isRunning) {
    return (
      <div className="rounded-lg border border-blue-200 bg-white px-3 py-2 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
            title="Show details"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 5.5L7 8.5L10 5.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="flex-1 h-2 overflow-hidden rounded-full bg-blue-100">
            <div
              className="h-full rounded-full bg-blue-500 transition-all duration-300"
              style={{ width: `${Math.max(pct, 3)}%` }}
            />
          </div>
          {progress.totalChecks > 0 && (
            <span className="text-gray-600 font-medium text-sm tabular-nums shrink-0">
              {progress.completedChecks}/{progress.totalChecks}
            </span>
          )}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded border border-gray-300 px-2.5 py-0.5 text-xs font-medium text-gray-500 transition-colors hover:border-red-300 hover:text-red-600 shrink-0"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  }

  // Expanded (default)
  return (
    <div className="rounded-lg border border-blue-200 bg-white p-3 shadow-sm">
      <div className="flex items-center justify-between text-sm">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {isRunning && (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                title="Collapse"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4 8.5L7 5.5L10 8.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <span className="font-medium text-gray-800">{stepLabel}</span>
          </div>
          {displayChecks.length > 0 && (
            <div className="mt-1 flex flex-col gap-0.5">
              <AnimatePresence mode="popLayout">
                {displayChecks.map((check) => (
                  <motion.div
                    key={check.checkName}
                    layout={!check.isCompleting}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: 8 }}
                    transition={{
                      opacity: { duration: 0.4 },
                      y: { duration: 0.4 },
                      x: { duration: 0.3 },
                      layout: { duration: 0.5, ease: "easeInOut" },
                    }}
                  >
                    <CheckRow
                      check={check}
                      isCompleting={check.isCompleting}
                      isPending={check.isPending}
                      isFailed={check.isFailed}
                      isSkipped={check.isSkipped}
                      errorMessage={check.errorMessage}
                      progressThresholdBytes={progressThresholdBytes}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
          {!progress.activeChecks?.length &&
            !progress.apiProgress &&
            progress.apiStatus && (
              <p className="text-xs text-gray-500 mt-0.5">
                {progress.apiStatus}
              </p>
            )}
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-3">
          {elapsed && (
            <span className="text-gray-400 tabular-nums text-xs">
              {elapsed}
            </span>
          )}
          {progress.totalChecks > 0 && (
            <span className="text-gray-600 font-medium text-sm tabular-nums">
              {progress.completedChecks}/{progress.totalChecks}
            </span>
          )}
          {isRunning && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded border border-gray-300 px-2.5 py-0.5 text-xs font-medium text-gray-500 transition-colors hover:border-red-300 hover:text-red-600"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
      {isRunning && (
        <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-blue-100">
          <div
            className="h-full rounded-full bg-blue-500 transition-all duration-300"
            style={{ width: `${Math.max(pct, 3)}%` }}
          />
        </div>
      )}
      {/* ⑤ ライブ実況ログ: チェック完了ごとの1行「速報」（ローカルLLM・参考）。
          ローカル不在なら空のまま出ない。確定結果は下の正規表示が権威。 */}
      {isRunning && liveLog.length > 0 && (
        <div className="mt-2.5 max-h-28 overflow-y-auto rounded-md border border-indigo-100 bg-indigo-50/40 px-3 py-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-400">
            速報（AI・参考）
          </div>
          <ul className="space-y-0.5">
            {liveLog.map((line, i) => (
              <li key={i} className="text-[11px] leading-snug text-indigo-700">
                <span className="mr-1 text-indigo-300">›</span>
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}
      {progress.status === "error" && progress.error && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm text-red-700">{progress.error}</p>
            <div className="flex items-center gap-2 shrink-0">
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="text-sm font-medium text-red-700 hover:text-red-900 underline whitespace-nowrap"
                >
                  Rerun full check from start
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                  title="Dismiss"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M1 1l12 12M13 1L1 13"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
